import { useCallback, useEffect, useState } from 'react';

import { fetchJson } from '@api/ApiHelper';

export interface CustomPermission {
  key?: string;
  label: string;
  asset_approve: boolean;
  add_to_shares: boolean;
  view_asset_usage: boolean;
  view_collection_usage: boolean;
}

export interface CustomRoleWithUsage extends CustomPermission {
  key: string;
  userCount?: number; // Number of users assigned to this role
}

export interface CustomPermissionResponse {
  data: {
    id: string;
    type: 'custom_permissions';
    attributes: CustomPermission;
  };
}

export interface CustomPermissionsListResponse {
  data: Array<{
    id: string;
    type: 'custom_permissions';
    attributes: CustomPermission;
  }>;
}

// Create a new custom permission
export const createCustomPermission = async (
  organizationKey: string,
  permission: Omit<CustomPermission, 'key'>
): Promise<CustomPermissionResponse> => {
  return fetchJson<CustomPermissionResponse>({
    url: `/api/v4/organizations/${organizationKey}/custom_permissions`,
    method: 'POST',
    body: {
      data: {
        attributes: permission,
      },
    },
  });
};

// Update an existing custom permission
export const updateCustomPermission = async (
  organizationKey: string,
  key: string,
  permission: Omit<CustomPermission, 'key'>
): Promise<CustomPermissionResponse> => {
  return fetchJson<CustomPermissionResponse>({
    url: `/api/v4/organizations/${organizationKey}/custom_permissions/${key}`,
    method: 'PUT',
    body: {
      data: {
        attributes: permission,
      },
    },
  });
};

// Delete a custom permission
export const deleteCustomPermission = async (
  organizationKey: string,
  key: string
): Promise<void> => {
  return fetchJson<void>({
    url: `/api/v4/organizations/${organizationKey}/custom_permissions/${key}`,
    method: 'DELETE',
  });
};

// Get all custom permissions for an organization
export const getCustomPermissions = async (
  organizationKey: string
): Promise<CustomPermissionsListResponse> => {
  return fetchJson<CustomPermissionsListResponse>({
    url: `/api/v4/organizations/${organizationKey}/custom_permissions`,
    method: 'GET',
  });
};

interface UseCustomRoleReturn {
  customRole: CustomRoleWithUsage | null;
  loading: boolean;
  error: string | null;
  refreshCustomRole: () => Promise<void>;
}

/**
 * Hook to fetch and manage custom role data for an organization
 * 
 * @param organizationKey - The organization key to fetch custom roles for
 * @returns Object containing the custom role data, loading state, error state, and refresh function
 */
export const useCustomRole = (organizationKey?: string): UseCustomRoleReturn => {
  const [customRole, setCustomRole] = useState<CustomRoleWithUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get organization key from global BFG object if not provided
  const orgKey = organizationKey ||
    (window as unknown as { BFG?: { organization_key?: string } }).BFG?.organization_key;

  const fetchCustomRole = useCallback(async (): Promise<void> => {
    if (!orgKey) {
      setError('Organization key is required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await getCustomPermissions(orgKey);

      // Since there can only be one custom role, get the first one
      const roleData = response.data[0];
      if (roleData) {
        const roleWithUsage: CustomRoleWithUsage = {
          ...roleData.attributes,
          key: roleData.id,
          userCount: 0, // TODO: Implement actual user count fetching
        };
        setCustomRole(roleWithUsage);
      } else {
        setCustomRole(null);
      }

      setError(null);
    } catch (err) {
      console.error('Error fetching custom role:', err);
      setError('Failed to load custom role');
    } finally {
      setLoading(false);
    }
  }, [orgKey]);

  useEffect(() => {
    void fetchCustomRole();
  }, [fetchCustomRole]);

  return {
    customRole,
    loading,
    error,
    refreshCustomRole: fetchCustomRole
  };
};
